import ClickEventTrigger from 'components/ClickEventTrigger';
import cx from 'classnames';
// @ts-ignore
import Input from 'components/Input';
// @ts-ignore
import Label from 'components/Label';
import Text from 'components/Text';
import * as React from 'react';

import './styles/Checkbox.scss';

/**
 * ## Checkbox input with a [Label](/internal/components/Label).
 * ### Use checkboxes when you would like the user to make none, one, or multiple selections out of a limited pool of options (6 or fewer). You can use a [CheckboxList](/internal/components/CheckboxList) to easily lay out several `Checkbox`es.
 *
 * ```example
 * <Field>
 *   <Checkbox
 *     label="Checkbox label"
 *   />
 *   <Checkbox
 *     checked
 *     label="Checkbox label"
 *   />
 *   <Checkbox
 *     checked
 *     disabled
 *     label="Checkbox label"
 *   />
 * </Field>
 * ```
 *
 * ```example
 * <Field>
 *   <Checkbox
 *     label="Checkbox label"
 *     labelNote="Checkbox label note"
 *   />
 *   <Checkbox
 *     checked
 *     label="Checkbox label"
 *   />
 *   <Checkbox
 *     checked
 *     divided
 *     label="Checkbox label"
 *   />
 *   <Checkbox
 *     label="Checkbox label"
 *   />
 * </Field>
 * ```
 */

export type Props = {
  className?: string;
  children?: React.ReactNode;
  value?: any;

  /** Vertically align indicator with label */
  center?: boolean;
  /** Native */
  checked?: boolean;
  /** Native */
  disabled?: boolean;
  /** Separates proceeding Checkboxes */
  divided?: boolean;
  flush?: boolean;
  label?: React.ReactElement<any> | string;
  /** Supplemental label text */
  labelNote?: React.ReactNode;
  /** Native */
  name?: string;
  nonTextLabel?: boolean;
  eventElementName?: string;
  eventScreenName?: string;
};

class Checkbox extends React.Component<React.InputHTMLAttributes<any> & Props> {
  isChecked = () => {
    // @ts-ignore -- old refs behaviour
    return this.refs.input.getInputNode().checked;
  };

  renderInput = (properties: Object) => {
    const { checked, disabled, name } = this.props;
    const input = (
      <Input
        {...properties}
        checked={checked}
        disabled={disabled}
        ref="input"
        type="checkbox"
        className="Checkbox-input"
      />
    );

    if (name && !disabled && checked) {
      const { eventScreenName } = this.props;
      const eventElementName = this.props.eventElementName || 'Checkbox';
      const eventProperties = {
        [name]: this.props.value,
        toggle: this.props.checked ? 'no' : 'yes',
      };

      return (
        <ClickEventTrigger
          eventElementName={eventElementName}
          eventProperties={eventProperties}
          eventScreenName={eventScreenName}>
          {input}
        </ClickEventTrigger>
      );
    } else {
      return input;
    }
  };

  renderLabelNote = () => {
    const { labelNote } = this.props;
    if (labelNote) {
      return <div className="Checkbox-labelNote">{labelNote}</div>;
    } else {
      return null;
    }
  };

  render() {
    const {
      center,
      className,
      divided,
      flush,
      label,
      nonTextLabel,
      children,
      disabled,
      ...others
    } = this.props;

    return (
      <Label
        className={cx(
          'Checkbox',
          {
            'Checkbox--center': center,
            'Checkbox--disabled': disabled,
            'Checkbox--divided': divided,
            'Checkbox--flush': flush,
            'Checkbox--noLabel': !label && !children,
            'Checkbox--nonTextLabel': nonTextLabel,
          },
          className
        )}>
        {this.renderInput(others)}
        <span className="Checkbox-indicator" />
        <Text color="thunder" className="Checkbox-label">
          {label}
          {children}
          {this.renderLabelNote()}
        </Text>
      </Label>
    );
  }
}

export default Checkbox;
